import { MINOR_PER_MAJOR, formatMilli, formatMinor, type Settings } from '@garage/shared'

/**
 * Display formatting bound to the current settings.
 *
 * Money formatting needs the currency symbol and decimal places, which live in
 * settings, so components take a formatter rather than calling formatMinor with
 * hardcoded options and drifting apart.
 */
export interface Formatters {
  /** "AED 1,234.50" */
  money: (minor: number) => string
  /** "1,234.50" — for table cells, where the column header carries the currency. */
  amount: (minor: number) => string
  /** "2.5" */
  quantity: (milli: number) => string
  /**
   * Short form for chart axes: "1.2k", "3M", "450".
   *
   * Axis ticks must read in the SAME unit as the tooltips and the table beside
   * them; labelling raw minor units made a 1,102.50 AED month read as "110k".
   */
  compact: (minor: number) => string
  /** Date only, in the configured format. */
  date: (iso: string | null | undefined) => string
  /** Date and time, for timelines. */
  dateTime: (iso: string | null | undefined) => string
}

/** At most one decimal, and no trailing ".0" — axis labels must stay short. */
function trim(value: number): string {
  return (Math.round(value * 10) / 10).toString()
}

export function makeFormatters(settings: Settings | undefined): Formatters {
  const decimals = settings?.currencyDecimals ?? 2
  const symbol = settings?.currencySymbol ?? 'AED'
  const pattern = settings?.dateFormat ?? 'dd/MM/yyyy'

  const formatDate = (iso: string | null | undefined, withTime: boolean): string => {
    if (!iso) return '—'
    const d = new Date(iso)
    if (Number.isNaN(d.getTime())) return '—'
    const dd = String(d.getDate()).padStart(2, '0')
    const mm = String(d.getMonth() + 1).padStart(2, '0')
    const yyyy = String(d.getFullYear())
    let out: string
    if (pattern === 'yyyy-MM-dd') out = `${yyyy}-${mm}-${dd}`
    else if (pattern === 'MM/dd/yyyy') out = `${mm}/${dd}/${yyyy}`
    else out = `${dd}/${mm}/${yyyy}`
    if (!withTime) return out
    const hh = String(d.getHours()).padStart(2, '0')
    const min = String(d.getMinutes()).padStart(2, '0')
    return `${out} ${hh}:${min}`
  }

  return {
    money: (minor) => formatMinor(minor, { symbol, decimals }),
    amount: (minor) => formatMinor(minor, { decimals }),
    quantity: (milli) => formatMilli(milli),
    compact: (minor) => {
      const major = minor / MINOR_PER_MAJOR
      const abs = Math.abs(major)
      if (abs >= 1_000_000) return `${trim(major / 1_000_000)}M`
      if (abs >= 1_000) return `${trim(major / 1_000)}k`
      return trim(major)
    },
    date: (iso) => formatDate(iso, false),
    dateTime: (iso) => formatDate(iso, true),
  }
}
